import React, { useState, useMemo } from "react";
import { Table, Tabs, Badge, Tooltip, Alert } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";

const ErrorLogsTable = ({ logsData, summaryData, loading }) => {
  const [activeTab, setActiveTab] = useState("all");

  const filteredData = useMemo(() => {
    if (activeTab === "all") return logsData;
    return logsData.filter((log) => log?.status === activeTab);
  }, [logsData, activeTab]);

  const columns = [
    {
      title: "Row Number(s)",
      dataIndex: "row_indexes",
      key: "row_indexes",
      width: 120,
      render: (rows) => (
        <span className="font-semibold text-gray-700">
          {Array.isArray(rows) ? rows.join(", ") : rows}
        </span>
      ),
    },
    {
      title: "Client Name",
      dataIndex: "client_name",
      key: "client_name",
      width: 200,
      render: (name) => <span className="text-gray-800">{name || "-"}</span>,
    },
    {
      title: "Field",
      dataIndex: "fieldname",
      key: "fieldname",
      width: 120,
      render: (field) => (
        <span className="text-gray-600 text-sm">{field || "-"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => {
        const statusConfig = {
          success: {
            color: "green",
            icon: <CheckCircleOutlined />,
            text: "Success",
          },
          error: {
            color: "red",
            icon: <CloseCircleOutlined />,
            text: "Error",
          },
          warning: {
            color: "orange",
            icon: <WarningOutlined />,
            text: "Warning",
          },
        };

        const config = statusConfig[status] || statusConfig.error;

        return (
          <Badge
            status={
              status === "success"
                ? "success"
                : status === "error"
                ? "error"
                : "warning"
            }
            text={config.text}
          />
        );
      },
    },
    {
      title: "Message(s)",
      dataIndex: "messages",
      key: "messages",
      render: (messages) => {
        if (!messages || messages.length === 0) {
          return <span className="text-green-600">Successfully imported</span>;
        }

        const messageList = Array.isArray(messages) ? messages : [messages];

        return (
          <div className="flex flex-col space-y-1">
            {messageList.map((msg, idx) => {
              const messageText = typeof msg === "string" ? msg : msg?.message;

              return (
                <Tooltip key={idx} title={messageText} placement="topLeft">
                  <div
                    className="text-sm text-red-600 truncate max-w-md"
                    dangerouslySetInnerHTML={{ __html: messageText }}
                  />
                </Tooltip>
              );
            })}
          </div>
        );
      },
    },
  ];

  // Create tab items with counts
  const tabItems = [
    {
      key: "all",
      label: (
        <span className="flex items-center gap-2">
          <span>All Records</span>
          <Badge
            count={summaryData?.total_records || 0}
            style={{ backgroundColor: "#1890ff" }}
          />
        </span>
      ),
    },
    {
      key: "success",
      label: (
        <span className="flex items-center gap-2">
          <CheckCircleOutlined />
          <span>Success</span>
          <Badge
            count={summaryData?.success_count || 0}
            style={{ backgroundColor: "#52c41a" }}
          />
        </span>
      ),
    },
    {
      key: "error",
      label: (
        <span className="flex items-center gap-2">
          <CloseCircleOutlined />
          <span>Errors</span>
          <Badge
            count={summaryData?.error_count || 0}
            style={{ backgroundColor: "#ff4d4f" }}
          />
        </span>
      ),
    },
    {
      key: "warning",
      label: (
        <span className="flex items-center gap-2">
          <WarningOutlined />
          <span>Warnings</span>
          <Badge
            count={summaryData?.warning_count || 0}
            style={{ backgroundColor: "#faad14" }}
          />
        </span>
      ),
    },
  ];

  return (
    <div className="mt-6">
      <Badge
        status={
          summaryData?.status === "Success"
            ? "success"
            : summaryData?.status === "Failed"
            ? "error"
            : "processing"
        }
        text={summaryData?.status}
        className="text-sm font-medium"
      />
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        className="error-logs-tabs"
        style={{ marginTop: "16px" }}
      />

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey={(record, index) =>
          `${record.log_name || index}-${record.row_indexes?.join("-")}`
        }
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} records`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        scroll={{ x: 1000, y: 200 }}
        bordered
        size="middle"
      />

      <style dangerouslySetInnerHTML={{ __html: `
        .error-logs-tabs .ant-tabs-tab {
          padding: 12px 20px;
          font-weight: 500;
        }
        .error-logs-tabs .ant-tabs-tab-active {
          background-color: #f0f5ff;
        }
      ` }} />
    </div>
  );
};

export default ErrorLogsTable;
